/**
 * Google OAuth Callback Handler
 * Handles the redirect from Google OAuth and exchanges the code for user session
 */
import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/App";
import api from "@/lib/api";

const GoogleCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processGoogleCallback = async () => {
      const code = searchParams.get("code");
      const error = searchParams.get("error");

      if (error) {
        console.error("Google OAuth error:", error);
        toast.error("Google authentication was cancelled or failed");
        navigate("/login");
        return;
      }

      if (!code) {
        console.error("No authorization code received");
        toast.error("Authentication failed - no code received");
        navigate("/login");
        return;
      }

      try {
        // Determine the redirect URI based on current origin
        const redirectUri = window.location.origin + "/auth/google/callback";

        // Exchange code for user session
        const response = await api.post("/auth/google/callback", {
          code: code,
          redirect_uri: redirectUri
        });

        // Store the JWT token
        if (response.data.token) {
          localStorage.setItem("wysspr_token", response.data.token);
        }

        // Set user in auth context
        setUser(response.data);

        toast.success(`Welcome, ${response.data.name}!`);
        
        // Redirect to dashboard
        navigate("/dashboard", { state: { user: response.data } });
      } catch (error) {
        console.error("Google auth callback error:", error);
        toast.error(error.response?.data?.detail || "Authentication failed");
        navigate("/login");
      }
    };

    processGoogleCallback();
  }, [navigate, setUser, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="text-center">
        <div className="animate-spin w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-slate-300 text-lg">Completing Google sign-in...</p>
        <p className="text-slate-500 text-sm mt-2">Please wait while we verify your account</p>
      </div>
    </div>
  );
};

export default GoogleCallback;
